/* eslint-disable no-nested-ternary */
import currency from "currency.js";
import { useCallback, useMemo } from "react";
import {
  OrderTypeResponse,
  SideResponse,
} from "../../../codegen-api";
import {
  BACKGROUND_COLORS,
  COLORS,
  LAYER_COLORS,
  TEXT_COLORS,
} from "../../../constants/design/colors";
import { FONT_SIZE } from "../../../constants/design/fontSize";
import { FormValidatorKeysEnum } from "../../../enums/form";
import { IPriceSize } from "../../../interfaces/Orderbook";
import { ITradeInfo } from "../../../interfaces/TradeInfo";
import { getContractPriceStep } from "../../../utils/instruments";
import { getValueOfContractsAmount } from "../../../utils/orderbook";
import { sideColors } from "../../../utils/sideColors";
import { ISegmentedControlConfig } from "../../shared/SegmentedControl";
import { BoostTitle, BoostValueWrapper } from "../style";
import { calculateFarmBoostMultiplier } from "../../../utils/math";
import BoostedVolumeTooltip from "../../shared/BoostedVolumeTooltip";
import { useOptionsTradeFormSetup } from "./useOptionsTradeFormSetup";

type SetupResult = ReturnType<typeof useOptionsTradeFormSetup>;

export function useOptionsTradeFormComputed({
  selectedInstrument,
  orderSide,
  commonFormTranslations,
  orderType,
  orderbookData,
  price,
  positionsMap,
  amount,
  errors,
  optionsTradeFormTranslations,
  marginData,
  farmData,
  index,
}: SetupResult) {
  const contractPriceStep = useMemo(
    () => getContractPriceStep(selectedInstrument),
    [selectedInstrument]
  );

  const buySellSegmentProps = useMemo(() => {
    const config: ISegmentedControlConfig = {
      theme: "outline",
      color: sideColors(orderSide).one,
      widthType: "fullWidth",
      backgroundColor: BACKGROUND_COLORS.eight,
      activeBackgroundColor: sideColors(orderSide).five,
      borderRadius: "10px",
      button: {
        height: 40,
        fontSize: FONT_SIZE.two,
      },
    };
    return {
      config,
      segments: [
        {
          value: String(SideResponse.Buy),
          display: commonFormTranslations("buy"),
          textColor:
            orderSide === SideResponse.Buy
              ? COLORS.up.one
              : TEXT_COLORS.three,
        },
        {
          value: String(SideResponse.Sell),
          display: commonFormTranslations("sell"),
          textColor:
            orderSide === SideResponse.Sell
              ? COLORS.down.one
              : TEXT_COLORS.three,
        },
      ],
    };
  }, [orderSide, commonFormTranslations]);

  const limitMarketSegmentProps = useMemo(() => {
    const config: ISegmentedControlConfig = {
      widthType: "fullWidth",
      backgroundColor: LAYER_COLORS.two,
      activeBackgroundColor: LAYER_COLORS.three,
      borderRadius: "10px",
      button: {
        height: 32,
        fontSize: FONT_SIZE.one,
      },
    };
    return {
      config,
      segments: [
        {
          value: OrderTypeResponse.Market,
          display: commonFormTranslations("market"),
          textColor:
            orderType === OrderTypeResponse.Market
              ? TEXT_COLORS.one
              : TEXT_COLORS.three,
        },
        {
          value: OrderTypeResponse.Limit,
          display: commonFormTranslations("limit"),
          textColor:
            orderType === OrderTypeResponse.Limit
              ? TEXT_COLORS.one
              : TEXT_COLORS.three,
        },
      ],
    };
  }, [orderType, commonFormTranslations]);

  const currentPosition = useMemo(
    () =>
      selectedInstrument
        ? positionsMap[selectedInstrument.instrument_name]
        : undefined,
    [positionsMap, selectedInstrument]
  );

  // Utility callback to use when verifying input
  const calculateTotalValueWithSize = useCallback(
    (amountStr: string) => {
      let total = 0;
      if (orderType === OrderTypeResponse.Market) {
        total = getValueOfContractsAmount(
          orderSide,
          orderSide === SideResponse.Buy
            ? (orderbookData?.asks as IPriceSize[]) || []
            : (orderbookData?.bids as IPriceSize[]) || [],
          Number(amountStr)
        );
      } else {
        total = amountStr ? Number(amountStr) * Number(price) : total;
      }
      return total;
    },
    [orderSide, orderType, orderbookData?.asks, orderbookData?.bids, price]
  );

  // Total value in USD
  const totalValue = useMemo(
    () => calculateTotalValueWithSize(amount),
    [calculateTotalValueWithSize, amount]
  );

  const orderValueTooSmall = useMemo(() => {
    if (errors?.amount?.type === FormValidatorKeysEnum.orderValueTooSmall) {
      return true;
    }
    return false;
  }, [errors?.amount?.type]);

  const notEnoughBalanceError = useMemo(() => {
    if (errors?.amount?.type === FormValidatorKeysEnum.notEnoughBalance) {
      return true;
    }
    return false;
  }, [errors?.amount?.type]);

  const tradeInfo = useMemo<ITradeInfo[]>(() => {
    const totalInfo: ITradeInfo = {
      title: optionsTradeFormTranslations("total"),
      value: totalValue ? currency(totalValue).format() : "---",
      warningOrError:
        notEnoughBalanceError || orderValueTooSmall ? "error" : undefined,
    };

    const premiumsInfo: ITradeInfo = {
      title: optionsTradeFormTranslations("premiums_earned"),
      value: totalValue ? currency(totalValue).format() : "---",
    };

    const marginRequiredInfo: ITradeInfo = {
      title: optionsTradeFormTranslations("margin_required"),
      value: marginData?.initial_margin
        ? currency(marginData?.initial_margin).format()
        : "---",
      warningOrError:
        notEnoughBalanceError || orderValueTooSmall ? "error" : undefined,
    };

    // ================ MARKET ORDERS ================
    if (orderType === OrderTypeResponse.Market) {
      // MARKET BUY/SELL ORDER
      if (orderSide === SideResponse.Buy) {
        return [totalInfo];
      }
      return [premiumsInfo, marginRequiredInfo];
    }

    // ================ LIMIT ORDERS ================
    if (orderSide === SideResponse.Buy) {
      // LIMIT BUY ORDER
      return [totalInfo];
    }
    // LIMIT SELL ORDER
    return [premiumsInfo, marginRequiredInfo];
  }, [
    optionsTradeFormTranslations,
    orderType,
    orderSide,
    totalValue,
    notEnoughBalanceError,
    marginData,
    orderValueTooSmall,
  ]);

  const bottomTradeInfo = useMemo<ITradeInfo[]>(() => {
    const notionalVolume = Number(amount) * Number(index?.price || 0);
    const farmBoost = farmData?.farm_boost;
    const totalVolume =
      farmData && notionalVolume
        ? Number(farmData.trailing_volume || 0) + notionalVolume
        : notionalVolume;
    const farmBoostAfterTrade = calculateFarmBoostMultiplier(totalVolume);
    const boostedVolume = notionalVolume * farmBoostAfterTrade;
    const boostIncrease =
      farmBoost && notionalVolume && totalVolume
        ? farmBoostAfterTrade - Number(farmBoost)
        : 0;
    const boostIncreasePercentage =
      farmBoost && boostIncrease
        ? String((boostIncrease / Number(farmBoost)) * 100)
        : undefined;
    return [
      {
        key: "boosted_volume",
        title: (
          <BoostTitle>
            <div>{commonFormTranslations("boosted_volume")}</div>
            <BoostedVolumeTooltip
              tradeFormVolume={String(totalVolume)}
              tradeFormBoostedVolume={String(boostedVolume)}
              boostIncrease={String(boostIncrease)}
              boostIncreasePercentage={boostIncreasePercentage}
              farmData={farmData}
            />
          </BoostTitle>
        ),
        value: (
          <BoostValueWrapper>
            {boostedVolume ? currency(boostedVolume).format() : "---"}
          </BoostValueWrapper>
        ),
      },
    ];
  }, [amount, commonFormTranslations, farmData, index?.price]);

  return {
    contractPriceStep,
    buySellSegmentProps,
    limitMarketSegmentProps,
    currentPosition,
    calculateTotalValueWithSize,
    totalValue,
    orderValueTooSmall,
    notEnoughBalanceError,
    tradeInfo,
    bottomTradeInfo,
  };
}
